import React, { useState, useEffect } from 'react';
import { plansApi } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  CurrencyInr, Plus, PencilSimple, Trash, Buildings, Star,
  Users, MapPin, HardDrive, Package, Check, Crown
} from '@phosphor-icons/react';
import { Toaster, toast } from 'sonner';

const ALL_MODULES = [
  { value: 'claims', label: 'Claims' },
  { value: 'patients', label: 'Patients' },
  { value: 'payers', label: 'Payers/TPA' },
  { value: 'preauth', label: 'Pre-Auth' },
  { value: 'denials', label: 'Denials' },
  { value: 'ar', label: 'AR & Collections' },
  { value: 'payments', label: 'Payments' },
  { value: 'reports', label: 'Reports' },
  { value: 'display_board', label: 'Display Board' },
  { value: 'workflow', label: 'Workflow' },
];

const PLAN_COLORS = {
  trial: { bg: 'bg-orange-50', border: 'border-orange-200', accent: 'text-orange-600', badge: 'bg-orange-100 text-orange-700', gradient: 'from-orange-500 to-amber-500' },
  basic: { bg: 'bg-slate-50', border: 'border-slate-200', accent: 'text-slate-600', badge: 'bg-slate-100 text-slate-700', gradient: 'from-slate-500 to-slate-600' },
  standard: { bg: 'bg-sky-50', border: 'border-sky-200', accent: 'text-sky-600', badge: 'bg-sky-100 text-sky-700', gradient: 'from-sky-500 to-blue-600' },
  professional: { bg: 'bg-violet-50', border: 'border-violet-200', accent: 'text-violet-600', badge: 'bg-violet-100 text-violet-700', gradient: 'from-violet-500 to-purple-600' },
  enterprise: { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-600', badge: 'bg-amber-100 text-amber-700', gradient: 'from-amber-500 to-yellow-500' },
};

const getColor = (code) => PLAN_COLORS[code] || PLAN_COLORS.basic;

const formatCurrency = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const emptyForm = {
  name: '', code: '', description: '',
  monthly_price: 0, annual_price: 0, currency: 'INR',
  max_users: 10, max_branches: 3, max_storage_gb: 5,
  modules_included: ['claims', 'patients', 'payers', 'reports', 'display_board'],
  features: '',
  is_active: true, is_popular: false, trial_days: 0, sort_order: 0,
};

const PlansPage = () => {
  const [plans, setPlans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [plansRes, summaryRes] = await Promise.all([
        plansApi.list(),
        plansApi.summary(),
      ]);
      setPlans(plansRes.data);
      setSummary(summaryRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingPlan(null);
    setForm({ ...emptyForm });
    setDialogOpen(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      name: plan.name || '',
      code: plan.code || '',
      description: plan.description || '',
      monthly_price: plan.monthly_price || 0,
      annual_price: plan.annual_price || 0,
      currency: plan.currency || 'INR',
      max_users: plan.max_users || 10,
      max_branches: plan.max_branches || 3,
      max_storage_gb: plan.max_storage_gb || 5,
      modules_included: plan.modules_included || [],
      features: (plan.features || []).join('\n'),
      is_active: plan.is_active !== false,
      is_popular: plan.is_popular || false,
      trial_days: plan.trial_days || 0,
      sort_order: plan.sort_order || 0,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.code) { toast.error('Plan name and code are required'); return; }
    try {
      const payload = {
        ...form,
        monthly_price: parseFloat(form.monthly_price) || 0,
        annual_price: parseFloat(form.annual_price) || 0,
        max_users: parseInt(form.max_users) || 10,
        max_branches: parseInt(form.max_branches) || 3,
        max_storage_gb: parseInt(form.max_storage_gb) || 5,
        trial_days: parseInt(form.trial_days) || 0,
        sort_order: parseInt(form.sort_order) || 0,
        features: form.features.split('\n').map(f => f.trim()).filter(Boolean),
      };
      if (editingPlan) {
        await plansApi.update(editingPlan.id, payload);
        toast.success(`Plan "${form.name}" updated`);
      } else {
        await plansApi.create(payload);
        toast.success(`Plan "${form.name}" created`);
      }
      setDialogOpen(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save plan');
    }
  };

  const handleDelete = async (plan) => {
    if (!window.confirm(`Delete "${plan.name}" plan? This cannot be undone.`)) return;
    try {
      await plansApi.delete(plan.id);
      toast.success(`Plan "${plan.name}" deleted`);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete plan');
    }
  };

  const toggleModule = (mod) => {
    setForm(f => ({
      ...f,
      modules_included: f.modules_included.includes(mod)
        ? f.modules_included.filter(m => m !== mod)
        : [...f.modules_included, mod]
    }));
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div></div>;

  return (
    <div className="space-y-6" data-testid="plans-page">
      <Toaster position="top-right" richColors />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800 font-['Outfit']" data-testid="plans-title">Subscription Plans</h1>
          <p className="text-sm text-slate-500 mt-1">Manage hospital subscription packages and pricing</p>
        </div>
        <Button className="bg-gradient-to-r from-sky-500 to-blue-600 text-white shadow-lg shadow-sky-500/25 gap-2" onClick={openCreate} data-testid="create-plan-btn">
          <Plus className="h-4 w-4" /> New Plan
        </Button>
      </div>

      {/* Revenue Summary */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="plan-summary">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="pt-5 pb-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Total Plans</p>
                <p className="text-2xl font-bold text-slate-800 mt-1 font-['Outfit']">{plans.length}</p>
              </div>
              <div className="h-10 w-10 rounded-xl bg-sky-50 flex items-center justify-center">
                <Package className="h-5 w-5 text-sky-600" weight="duotone" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="pt-5 pb-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Hospitals Subscribed</p>
                <p className="text-2xl font-bold text-slate-800 mt-1 font-['Outfit']">{summary.total_hospitals}</p>
              </div>
              <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Buildings className="h-5 w-5 text-emerald-600" weight="duotone" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="pt-5 pb-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Est. Monthly Revenue</p>
                <p className="text-2xl font-bold text-slate-800 mt-1 font-['Outfit']">{formatCurrency(summary.total_monthly_revenue)}</p>
              </div>
              <div className="h-10 w-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <CurrencyInr className="h-5 w-5 text-amber-600" weight="duotone" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Plans Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="plans-grid">
        {plans.map((plan) => {
          const c = getColor(plan.code);
          return (
            <Card key={plan.id} className={`bg-white border ${c.border} shadow-sm hover:shadow-md transition-all relative overflow-hidden`} data-testid={`plan-card-${plan.code}`}>
              {plan.is_popular && (
                <div className="absolute top-3 right-3">
                  <Badge className="bg-amber-100 text-amber-700 border-0 gap-1 text-xs"><Star className="h-3 w-3" weight="fill" /> Popular</Badge>
                </div>
              )}
              {!plan.is_active && (
                <div className="absolute top-3 right-3">
                  <Badge className="bg-red-100 text-red-600 border-0 text-xs">Inactive</Badge>
                </div>
              )}

              <div className={`h-1.5 bg-gradient-to-r ${c.gradient}`}></div>

              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <CardTitle className={`text-lg font-['Outfit'] ${c.accent}`}>{plan.name}</CardTitle>
                  <Badge className={`${c.badge} border-0 text-[10px]`}>{plan.code.toUpperCase()}</Badge>
                </div>
                <p className="text-xs text-slate-500 mt-0.5">{plan.description}</p>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Pricing */}
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold text-slate-800 font-['Outfit']">{formatCurrency(plan.monthly_price)}</span>
                    <span className="text-sm text-slate-400">/month</span>
                  </div>
                  {plan.annual_price > 0 && (
                    <p className="text-xs text-slate-500 mt-0.5">{formatCurrency(plan.annual_price)}/year (save {Math.round((1 - plan.annual_price / (plan.monthly_price * 12)) * 100)}%)</p>
                  )}
                </div>

                {/* Limits */}
                <div className="grid grid-cols-2 gap-2">
                  <div className={`text-center py-2 rounded-lg ${c.bg}`}>
                    <Users className={`h-4 w-4 mx-auto ${c.accent}`} weight="duotone" />
                    <p className="text-sm font-bold text-slate-800 mt-0.5">{plan.max_users}</p>
                    <p className="text-[10px] text-slate-500">Users</p>
                  </div>
                  <div className={`text-center py-2 rounded-lg ${c.bg}`}>
                    <MapPin className={`h-4 w-4 mx-auto ${c.accent}`} weight="duotone" />
                    <p className="text-sm font-bold text-slate-800 mt-0.5">{plan.max_branches}</p>
                    <p className="text-[10px] text-slate-500">Branches</p>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-1.5">
                  {(plan.features || []).slice(0, 5).map((f, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-600">
                      <Check className={`h-3.5 w-3.5 flex-shrink-0 ${c.accent}`} weight="bold" />
                      <span>{f}</span>
                    </div>
                  ))}
                  {(plan.features || []).length > 5 && (
                    <p className="text-[10px] text-slate-400 pl-5">+{plan.features.length - 5} more features</p>
                  )}
                </div>

                {/* Hospital count */}
                <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                  <div className="flex items-center gap-1.5 text-xs text-slate-500">
                    <Buildings className="h-3.5 w-3.5" />
                    <span>{plan.hospital_count || 0} hospital{(plan.hospital_count || 0) !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-sky-50 text-sky-600" onClick={() => openEdit(plan)} data-testid={`edit-plan-${plan.code}`}>
                      <PencilSimple className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-red-50 text-red-500" onClick={() => handleDelete(plan)} data-testid={`delete-plan-${plan.code}`}>
                      <Trash className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl">
              {editingPlan ? `Edit Plan: ${editingPlan.name}` : 'Create New Plan'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Identity */}
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Plan Name *</Label>
                <Input placeholder="e.g., Professional" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} data-testid="plan-name-input" /></div>
              <div><Label>Plan Code *</Label>
                <Input placeholder="e.g., professional" value={form.code} onChange={e => setForm(f => ({...f, code: e.target.value.toLowerCase().replace(/\s/g, '_')}))} disabled={!!editingPlan} data-testid="plan-code-input" /></div>
            </div>
            <div><Label>Description</Label>
              <Textarea placeholder="Brief plan description" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="h-16" /></div>

            {/* Pricing */}
            <div className="border-t pt-3">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Pricing</h3>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Monthly Price</Label>
                  <Input type="number" value={form.monthly_price} onChange={e => setForm(f => ({...f, monthly_price: e.target.value}))} data-testid="plan-monthly-price" /></div>
                <div><Label>Annual Price</Label>
                  <Input type="number" value={form.annual_price} onChange={e => setForm(f => ({...f, annual_price: e.target.value}))} /></div>
                <div><Label>Trial Days</Label>
                  <Input type="number" value={form.trial_days} onChange={e => setForm(f => ({...f, trial_days: e.target.value}))} /></div>
              </div>
            </div>

            {/* Limits */}
            <div className="border-t pt-3">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Limits</h3>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Max Users</Label>
                  <Input type="number" value={form.max_users} onChange={e => setForm(f => ({...f, max_users: e.target.value}))} /></div>
                <div><Label>Max Branches</Label>
                  <Input type="number" value={form.max_branches} onChange={e => setForm(f => ({...f, max_branches: e.target.value}))} /></div>
              </div>
            </div>

            {/* Modules */}
            <div className="border-t pt-3">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Included Modules</h3>
              <div className="grid grid-cols-5 gap-2">
                {ALL_MODULES.map(mod => (
                  <div key={mod.value} className="flex items-center gap-2">
                    <Checkbox id={`pm-${mod.value}`} checked={form.modules_included.includes(mod.value)}
                      onCheckedChange={() => toggleModule(mod.value)} />
                    <Label htmlFor={`pm-${mod.value}`} className="text-xs cursor-pointer">{mod.label}</Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            <div className="border-t pt-3">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Features (one per line)</h3>
              <Textarea placeholder="Up to 10 users&#10;2 branches&#10;Email support" value={form.features}
                onChange={e => setForm(f => ({...f, features: e.target.value}))} className="h-24 font-mono text-xs" />
            </div>

            {/* Options */}
            <div className="flex items-center gap-6 border-t pt-3">
              <div className="flex items-center gap-2">
                <Checkbox id="is-active" checked={form.is_active} onCheckedChange={v => setForm(f => ({...f, is_active: v}))} />
                <Label htmlFor="is-active" className="text-sm cursor-pointer">Active</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="is-popular" checked={form.is_popular} onCheckedChange={v => setForm(f => ({...f, is_popular: v}))} />
                <Label htmlFor="is-popular" className="text-sm cursor-pointer">Mark as Popular</Label>
              </div>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Sort Order</Label>
                <Input type="number" className="w-20 h-8" value={form.sort_order} onChange={e => setForm(f => ({...f, sort_order: e.target.value}))} />
              </div>
            </div>

            <Button className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white mt-2" onClick={handleSave} data-testid="save-plan-btn">
              {editingPlan ? 'Update Plan' : 'Create Plan'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlansPage;
